// Zod payload schemas for the module operations, generated from ./spec.ts (Guide §10.2). Shared by the phone and the server.
import { z } from 'zod';
import { zDate, zId, zIso, zMoneyPos, zQtyPos, zText } from '../sync/primitives';
import { MODULE_ENTITIES, type EntitySpec, type FieldSpec } from './spec';

function fieldSchema(fl: FieldSpec): z.ZodTypeAny {
  let s: z.ZodTypeAny;
  switch (fl.type) {
    case 'text': s = zText(fl.max ?? 200); break;
    case 'note': s = zText(fl.max ?? 2000); break;
    case 'phone': s = zText(20); break;
    case 'money': s = zMoneyPos; break;
    case 'qty': s = zQtyPos; break;
    case 'int': s = z.number().int().min(0).max(100_000); break;
    case 'date': s = zDate; break;
    case 'datetime': s = zIso; break;
    case 'bool': s = z.boolean(); break;
    case 'enum': s = z.enum(fl.options as [string, ...string[]]); break;
    case 'ref': s = zId; break;
    case 'json': s = z.union([z.array(z.unknown()), z.record(z.string(), z.unknown())]).refine((v) => JSON.stringify(v).length <= 20_000, 'too_large'); break;
  }
  if ((fl.type === 'text' || fl.type === 'note') && fl.req) s = (s as z.ZodString).min(1);
  if (fl.def !== undefined && fl.def !== null) return s.default(fl.def);
  return fl.req ? s : s.nullable().optional();
}

export function buildShape(spec: EntitySpec): Record<string, z.ZodTypeAny> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const fl of spec.fields) if (!fl.server) shape[fl.key] = fieldSchema(fl);
  return shape;
}

/** `*.upsert` payload of a document module (create, edit and soft delete) */
export const buildDocSchema = (spec: EntitySpec): z.ZodObject<Record<string, z.ZodTypeAny>> => z.object({ ...buildShape(spec), deleted: z.boolean().optional() });
/** `*.create` payload of a ledger module */
export const buildCreateSchema = (spec: EntitySpec): z.ZodObject<Record<string, z.ZodTypeAny>> => z.object(buildShape(spec));

export const zVoid = z.object({ reason: zText(200).optional() });

const nonZero = (s: z.ZodObject<Record<string, z.ZodTypeAny>>, key: string) => s.refine((v) => Number(v[key] as string) > 0, { message: 'amount_must_be_positive', path: [key] });

const spec = (entity: string): EntitySpec => MODULE_ENTITIES.find((e) => e.entity === entity)!;

export const zMfsTxnCreate = nonZero(buildCreateSchema(spec('mfs_txns')), 'amount');
export const zRechargeCreate = nonZero(buildCreateSchema(spec('recharges')), 'amount');
export const zPersonalEntryCreate = nonZero(buildCreateSchema(spec('personal_entries')), 'amount');
export const zLoanCreate = nonZero(buildCreateSchema(spec('loans')), 'amount');
export const zLoanRepay = nonZero(buildCreateSchema(spec('loan_repayments')), 'amount');

// ── v10: correcting personal records (edit = one atomic "void old + write new", so a half-done edit can never happen) ──
/** entityId = the entry being corrected; `newId` = the id of the corrected entry */
export const zPersonalEntryEdit = nonZero(buildCreateSchema(spec('personal_entries')).extend({ newId: zId }), 'amount');
/** entityId = a voided entry (recycle bin) */
export const zPersonalEntryRestore = z.object({});
/** entityId = the loan. Amount / account may change while nothing has been repaid beyond the new amount. */
export const zLoanUpdate = z.object({
  party: zText(80).min(1),
  partyPhone: zText(20).nullable().optional(),
  amount: zMoneyPos,
  moneyAccountId: zId.nullable().optional(),
  dueDate: zDate.nullable().optional(),
  note: zText(200).nullable().optional(),
}).refine((v) => Number(v.amount) > 0, { message: 'amount_must_be_positive', path: ['amount'] });
/** entityId = the loan; reverses its money and its repayments, keeps it in the recycle bin */
export const zLoanDelete = z.object({ reason: zText(200).optional() });
export const zLoanRestore = z.object({});
/** entityId = the repayment */
export const zLoanRepaymentVoid = z.object({ reason: zText(200).optional() });
/** entityId = the goal. deposit: money goes into the goal (moved into its account when it has one); withdraw: back out. */
export const zGoalContribute = z.object({
  kind: z.enum(['deposit', 'withdraw']),
  amount: zMoneyPos,
  /** where the money comes from (deposit) / goes to (withdraw); null = it already sits in the goal's account */
  accountId: zId.nullable().optional(),
  note: zText(200).nullable().optional(),
}).refine((v) => Number(v.amount) > 0, { message: 'amount_must_be_positive', path: ['amount'] });

/** Every operation the modules add → its schema. Document modules come straight from the spec. */
export const MODULE_OP_SCHEMAS: Record<string, z.ZodTypeAny> = {
  ...Object.fromEntries(MODULE_ENTITIES.filter((e) => e.kind === 'doc').map((e) => [e.op, buildDocSchema(e)])),
  'mfs_txn.create': zMfsTxnCreate,
  'mfs_txn.void': zVoid,
  'recharge.create': zRechargeCreate,
  'recharge.void': zVoid,
  'personal_entry.create': zPersonalEntryCreate,
  'personal_entry.void': zVoid,
  'loan.create': zLoanCreate,
  'loan.repay': zLoanRepay,
  'personal_entry.edit': zPersonalEntryEdit,
  'personal_entry.restore': zPersonalEntryRestore,
  'loan.update': zLoanUpdate,
  'loan.delete': zLoanDelete,
  'loan.restore': zLoanRestore,
  'loan_repayment.void': zLoanRepaymentVoid,
  'savings_goal.contribute': zGoalContribute,
};
/** Literal union of the module operations (kept explicit so OpType stays a closed union; a guard below fails fast if it drifts from the spec). */
export const MODULE_OP_TYPES = [
  'mfs_wallet.upsert', 'mfs_txn.create', 'mfs_txn.void', 'recharge.create', 'recharge.void', 'mfs_closing.upsert',
  'dining_table.upsert', 'rest_order.upsert', 'rest_order_item.upsert',
  'measurement_profile.upsert', 'tailoring_order.upsert',
  'property.upsert', 'lead.upsert', 'property_commission.upsert',
  'project.upsert', 'milestone.upsert', 'retainer.upsert',
  'land_parcel.upsert', 'crop_season.upsert',
  'personal_entry.create', 'personal_entry.void', 'savings_goal.upsert', 'loan.create', 'loan.repay',
  // v10
  'personal_entry.edit', 'personal_entry.restore', 'loan.update', 'loan.delete', 'loan.restore', 'loan_repayment.void', 'savings_goal.contribute',
] as const;
export type ModuleOpType = (typeof MODULE_OP_TYPES)[number];
if (MODULE_OP_TYPES.length !== Object.keys(MODULE_OP_SCHEMAS).length || MODULE_OP_TYPES.some((t) => !(t in MODULE_OP_SCHEMAS))) {
  throw new Error('MODULE_OP_TYPES is out of sync with modules/spec.ts');
}
/** Loosely typed payload of a module op: validated by the spec-generated schema, shaped by the spec. */
export type ModulePayload = Record<string, unknown>;
